import React, { useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import {
  Stepper,
  Step,
  StepLabel,
  StepContent,
  MobileStepper,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import "./HistoryPage.css";
import AOS from "aos";
import "aos/dist/aos.css";

const HistoryPage = () => {
  const { language } = useLanguage();
  const theme = useTheme();
  const [activeStep, setActiveStep] = React.useState(0);
  const [isMobile, setIsMobile] = React.useState(false);

  useEffect(() => {
    AOS.init({ duration: 800 });
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const steps = [
    {
      year: "1996",
      title: "Beginnings in Manjeri",
      title_ml: "മഞ്ചേരിയിൽ തുടക്കം",
      content:
        "Palliative care in Malappuram district started in 1996 with the establishment of the Cancer Pain & Palliative Care Association (CAPCA), a registered community-based organisation in Manjeri. The Manjeri Palliative Clinic quickly became a vital resource, attracting a large number of patients and prompting CAPCA to expand its services across the district.",
      content_ml:
        "മലപ്പുറം ജില്ലയിൽ പാലിയേറ്റീവ് പരിചരണം 1996-ൽ മഞ്ചേരിയിൽ രജിസ്റ്റർ ചെയ്ത കാൻസർ പെയ്ൻ & പാലിയേറ്റീവ് കെയർ അസോസിയേഷൻ (CAPCA) സ്ഥാപിക്കപ്പെട്ടതോടെ ആരംഭിച്ചു. മഞ്ചേരി പാലിയേറ്റീവ് ക്ലിനിക്ക് പെട്ടെന്ന് തന്നെ ഒരു പ്രാധാന്യവത്തായ സ്രോതസ്സായി മാറി. രോഗികളുടെ എണ്ണത്തിൽ വന്ന വർദ്ധനവ് കണ്ട് CAPCA-യെ ജില്ലവ്യാപകമായി സേവനങ്ങൾ വികസിപ്പിക്കാൻ പ്രേരിപ്പിച്ചു.",
    },
    {
      year: "1996–2000",
      title: "District-Wide Expansion",
      title_ml: "ജില്ലാ വ്യാപക വികസനം",
      content:
        "To meet growing demand, CAPCA launched awareness campaigns and training programs, fostering the creation of local palliative societies with trained volunteers. By 2000, approximately ten palliative care units were operational. CAPCA decentralised its services to ensure at least one palliative clinic in every panchayath and municipality, managed by Community based voluntary associations.",
      content_ml:
        "വളരുന്ന ആവശ്യങ്ങൾ നിറവേറ്റാൻ CAPCA അതിരാവിലെ ജാഗൃകത പരിപാടികളും പരിശീലന പരിപാടികളും ആരംഭിച്ചു. 2000-ഓടെ ഏകദേശം പത്ത് പാലിയേറ്റീവ് കെയർ യൂണിറ്റുകൾ പ്രവർത്തന സജ്ജമായിരുന്നു. ഓരോ ഗ്രാമപഞ്ചായത്തിലും മുനിസിപ്പാലിറ്റിയിലും ഒന്നു വീതം പാലിയേറ്റീവ് ക്ലിനിക്ക് ഉറപ്പാക്കുന്നതിനായി CAPCA അതിന്റെ സേവനങ്ങൾ വികേന്ദ്രീകരിച്ചു.",
    },
    {
      year: "2021–Present",
      title: "Focus on Manjeri Municipality",
      title_ml: "മഞ്ചേരി മുനിസിപ്പാലിറ്റിയിൽ കേന്ദ്രീകരണം",
      content:
        "Since 2021, CAPCA has concentrated its efforts in Manjeri Municipality, establishing local palliative groups and further decentralising the Manjeri Pain and Palliative Clinic into smaller units, with one clinic serving every 9-10 divisions of the Municipality. Independent societies, supported by local volunteers and funding, now operate these clinics, ensuring accessible, community-driven care.",
      content_ml:
        "2021-ൽ മുതൽ CAPCA മഞ്ചേരി മുനിസിപ്പാലിറ്റിയിൽ അതിന്റെ ശ്രമങ്ങൾ കേന്ദ്രീകരിച്ചു. പ്രാദേശിക പാലിയേറ്റീവ് സംഘങ്ങൾ സൃഷ്ടിച്ച് മഞ്ചേരി പെയിൻ ആൻഡ് പാലിയേറ്റീവ് ക്ലിനിക്കിനെ ചെറിയ യൂണിറ്റുകളായി വികേന്ദ്രീകരിച്ചു. ഓരോ 9-10 വാര്ഡുകൾക്കും ഒരൊറ്റ ക്ലിനിക്ക് വീതം സേവനം നൽകുന്ന രീതിയിൽ പ്രശസ്തമായ സൊസൈറ്റികൾ പ്രാദേശിക സന്നദ്ധ പ്രവർത്തകരുടെയും സഹായ നിധിയുടെയും സഹകരണത്തോടെ പ്രവർത്തിക്കുന്നു.",
    },
    {
      year: "Current",
      title: "CAPCA's Current Role",
      title_ml: "CAPCA-യുടെ ഇന്നത്തെ പങ്ക്",
      content: [
        {
          subtitle: "Coordinating Excellence in Palliative Care",
          text: "CAPCA serves as the coordinating body for voluntary palliative units and societies in Manjeri Municipality. A governing council, with representatives from each society, oversees operations. Monthly review meetings and on going training for nurses, doctors, and volunteers maintain high standards of care.",
        },
        {
          subtitle: "Centre for Development of Palliative Care (CDPC)",
          text: "The Centre for Development of Palliative Care (CDPC), under CAPCA, advances palliative care through:",
          list: [
            "Education and Training: Offering specialised courses for professionals and volunteers to enhance skills and deliver compassionate care.",
            "Research: Conducting studies to develop innovative, evidence-based practices that improve patient care and inform the palliative care community.",
          ],
        },
      ],
      content_ml: [
        {
          subtitle_ml: "പാലിയേറ്റീവ് പരിചരണത്തിൽ ഉന്നതി കൈവരിക്കാൻ ഏകോപനം",
          text_ml:
            "CAPCA മഞ്ചേരി മുനിസിപ്പാലിറ്റിയിലെ സന്നദ്ധ പാലിയേറ്റീവ് യൂണിറ്റുകളുടെയും സൊസൈറ്റികളുടെയും ഏകോപന ബോഡിയായി പ്രവർത്തിക്കുന്നു. ഓരോ സൊസൈറ്റിയുടെയും പ്രാതിനിധികൾ ചേർന്ന ഭരണസമിതി പ്രവർത്തനങ്ങൾ മേൽനോട്ടം വഹിക്കുന്നു. പ്രതിമാസ അവലോകനയോഗങ്ങളും നഴ്സുമാർ, ഡോക്ടർമാർ, സന്നദ്ധ പ്രവർത്തകർക്ക് പരിശീലനവും ഉയർന്ന പരിചരണ മാനദണ്ഡങ്ങളും നിലനിർത്തുന്നു.",
        },
        {
          subtitle_ml: "പാലിയേറ്റീവ് പരിചരണ വികസന കേന്ദ്രം (CDPC)",
          text_ml:
            "CAPCA-യുടെ കീഴിലുള്ള പാലിയേറ്റീവ് പരിചരണ വികസന കേന്ദ്രം (CDPC), ഇനിപ്പറയുന്ന വഴികളിലൂടെ പാലിയേറ്റീവ് പരിചരണത്തെ ശക്തിപ്പെടുത്തുന്നു:",
          list_ml: [
            "വിദ്യാഭ്യാസവും പരിശീലനവും: പ്രൊഫഷണലുകൾക്കും സന്നദ്ധ പ്രവർത്തകർക്കും പ്രത്യേക പരിശീലനങ്ങൾ നൽകുകയും അവരെ കൂടുതൽ പ്രാപ്തരാക്കുകയും സഹാനുഭൂതി നിറഞ്ഞ പരിചരണം നിർവഹിക്കാൻ സഹായിക്കുകയുമാണ്.",
            "പര്യവേക്ഷണയും ഗവേഷണവും: രോഗികളുടെ പരിചരണം മെച്ചപ്പെടുത്തുകയും പാലിയേറ്റീവ് പരിചരണ സമൂഹത്തിന് പുതിയ തെളിവ് അടങ്ങിയ രീതികൾ നൽകുകയും ചെയ്യുന്ന പഠനങ്ങൾ നടത്തുന്നു.",
          ],
        },
      ],
    },
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const pageTitle = language === "ml" ? "ഞങ്ങളുടെ ചരിത്രം" : "Our History";
  const bannerAlt = language === "ml" ? "ഞങ്ങളുടെ ചരിത്രം" : "Our History";

  return (
    <div className="history-page-container">
      {/* Banner Section */}
      <div data-aos="fade-up" className="history-banner">
        <img
          src="/history-banner.jpg"
          alt={bannerAlt}
          className="banner-img"
        />
      </div>
      <h1 className="history-main-title">{pageTitle}</h1>

      {/* Main Content */}
      <div className="history-content-container w-full">
        <Stepper
          activeStep={activeStep}
          orientation="vertical"
          sx={{ marginLeft: "20px" }}
        >
          {steps.map((step, index) => (
            <Step key={index} expanded={true}>
              <StepLabel
                StepIconProps={{
                  style: {
                    color: theme.palette.primary.main,
                    width: "40px",
                    height: "40px",
                    fontSize: "1.2rem",
                  },
                }}
                sx={{
                  "& .MuiStepLabel-label": {
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {step.year}
              </StepLabel>
              <StepContent
                TransitionProps={{ in: true }}
                sx={{ borderLeft: `2px solid ${theme.palette.primary.main}` }}
              >
                <div className="step-content" data-aos="fade-up">
                  <h2 className="section-title text-warning fw-bold">
                    {language === "ml" ? step.title_ml || step.title : step.title}
                  </h2>
                  {(() => {
                    const contentToRender =
                      language === "ml" ? step.content_ml ?? step.content : step.content;

                    if (Array.isArray(contentToRender)) {
                      return contentToRender.map((item, i) => (
                        <div key={i}>
                          <h3 className="sub-section-title text-info">
                            {language === "ml"
                              ? item.subtitle_ml || item.subtitle
                              : item.subtitle}
                          </h3>
                          <p className="section-text text-white">
                            {language === "ml" ? item.text_ml || item.text : item.text}
                          </p>
                          {item.list && (
                            <ul className="history-list">
                              {item.list.map((listItem, j) => (
                                <li key={j} className="section-text text-white">
                                  {listItem}
                                </li>
                              ))}
                            </ul>
                          )}
                          {item.list_ml && (
                            <ul className="history-list">
                              {item.list_ml.map((listItem, j) => (
                                <li key={j} className="section-text text-white">
                                  {listItem}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ));
                    }

                    return (
                      <p className="section-text text-white">{contentToRender}</p>
                    );
                  })()}
                </div>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </div>
    </div>
  );
};

export default HistoryPage;
